/**
 * Client Request ▸ **Service Tracking** (design v2.0 §12; prototype v46).
 *
 * The third question this module answers. Request Registration asks what a
 * client asked for; Service Allocation asks what dispatch must act on this
 * morning; this asks **what happened** — across every request, over a window of
 * time, read newest first.
 *
 * READ-ONLY BY DESIGN. There is no write endpoint behind this screen and no
 * editable cell on it. A service's STAGE is derived from what the field
 * actually did (dispatched → arrived → completed), so it can never be typed;
 * letting anyone set "Completed" by hand would make the history a claim rather
 * than a record. Everything editable lives on Allocation or the request itself.
 *
 * ONE STAGE RULE, THREE READERS — the Stage column, the stage filter and the
 * detail's lifecycle rail all call `serviceStage`, so they cannot disagree
 * about where a service has got to.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import './clientRequest.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import {
  DateRangeSelect, describeRange, type CustomRange, type RangeOpt,
} from '../modules/DateRangeSelect';
import { AuditTimeline } from '../audit/AuditTimeline';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { navigateToPage } from '../../layouts/navigateTo';
import {
  AUDIT_FORM_NAME, SERVICE_STAGES, fetchAuditTrail, fetchServiceDefinitions,
  fetchServiceTracking, serviceStage,
  recordServiceProgress,
  type AuditTrailRow, type ServiceCategoryDef, type ServiceTrackingRow,
} from './clientRequestApi';

/** BO UI Standard §17: a Tracking form opens on the last 3 months. */
const DEFAULT_RANGE_DAYS = 90;

/** Prototype v68's windows, in the shared picker's `combo` variant: the long
 *  name on the left, the short code on the right. Days rather than calendar
 *  months, the same arithmetic as every other Tracking form's shared range
 *  list — those keep their `1Y` label; only this screen reads `12M`, as v68
 *  does. */
const SERVICE_RANGES: RangeOpt[] = [
  { label: '1M',  days: 30,   name: 'Last month' },
  { label: '3M',  days: 90,   name: 'Last 3 months' },
  { label: '6M',  days: 180,  name: 'Last 6 months' },
  { label: '12M', days: 365,  name: 'Last 12 months' },
  { label: 'ALL', days: null, name: 'All time' },
];

export default function ServiceTrackingPage() {
  const [rows,    setRows]    = useState<ServiceTrackingRow[]>([]);
  const [defs,    setDefs]    = useState<ServiceCategoryDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number>();

  const [days,   setDays]   = useState<number | null>(DEFAULT_RANGE_DAYS);
  /** A custom From / To window, or null while a preset is active. */
  const [custom, setCustom] = useState<CustomRange | null>(null);
  const [query,  setQuery]  = useState('');
  const [cat,    setCat]    = useState('');
  const [stage,  setStage]  = useState('');
  /** The service open in the detail view; null = the list. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Row selection — the SAME leading checkbox column Request Registration
   *  has (shared AppDataGrid `selectable`), so the Client Request lists read
   *  alike. Cleared whenever the visible set changes: a ticked row filtered out
   *  of view must not stay silently selected. */
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  useEffect(() => { setSel(new Set()); }, [rows, query, cat, stage, days, custom]);

  const notify = useNotify();

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [t, d] = await Promise.all([fetchServiceTracking(), fetchServiceDefinitions()]);
      setRows(t);
      setDefs(d);
      setLoadMs(Math.round(performance.now() - t0));
    } catch (e) {
      notify.error(e instanceof Error ? `Could not load service tracking: ${e.message}`
                                      : 'Could not load service tracking.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const catName = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.name ?? code ?? '—', [defs]);
  const catTone = useCallback(
    (code?: string | null) => defs.find(d => d.code === code)?.tone ?? 'neutral', [defs]);

  /* The window's cut-off as a LOCAL calendar day, compared as YYYY-MM-DD text.
     ScheduledDate is a calendar day, not an instant, so comparing it as text
     against a locally-built boundary keeps a service on the day it is actually
     scheduled for — parsing either side into a Date would shift the boundary
     across midnight for anyone west of the server. */
  const cutoff = useMemo((): CustomRange | null => {
    // A custom window is already a pair of calendar days, compared the same way.
    if (custom) return custom;
    if (days === null) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return {
      from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
          + `-${String(d.getDate()).padStart(2, '0')}`,
      to: null,
    };
  }, [days, custom]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      // An undated service has not happened yet, so a window — preset or
      // custom — has nothing to say about it; it shows only under "All time".
      const day = r.scheduledDate?.slice(0, 10);
      if (cutoff !== null
          && (!day
              || (cutoff.from !== null && day < cutoff.from)
              || (cutoff.to   !== null && day > cutoff.to))) return false;
      if (cat && r.categoryCode !== cat) return false;
      if (stage && serviceStage(r).label !== stage) return false;
      if (!q) return true;
      return [r.serviceNo, r.requestNo, r.clientName, r.projectCode, catName(r.categoryCode),
              r.siteAddress, r.assignedToName, r.routeCode, r.jobNo]
        .some(v => (v ?? '').toLowerCase().includes(q));
    });
  }, [rows, cutoff, cat, stage, query, catName]);

  const columns: AppColumn<ServiceTrackingRow>[] = useMemo(() => [
    { field: 'serviceNo', header: 'Service', width: 150,
      text: r => r.serviceNo ?? '',
      // A button, not coloured text: §18.6 keeps every grid cell on the one ink
      // token (a blanket !important enforces it) and says IDs stay ink. The row
      // itself opens the record, so this exists for keyboard reach, and hover
      // underline carries the affordance without colour.
      render: r => (
        <button type="button" className="cr-linkcell"
                onClick={e => { e.stopPropagation(); setOpenId(r.id); }}>
          {r.serviceNo ?? '—'}
        </button>
      ) },
    { field: 'categoryCode', header: 'Category', width: 130,
      text: r => catName(r.categoryCode),
      render: r => <span className={`cr-chip ${catTone(r.categoryCode)}`}>
        {catName(r.categoryCode)}</span> },
    { field: 'clientName', header: 'Client', width: 190,
      text: r => r.clientName ?? '',
      render: r => r.clientName
        ? <>{r.clientName}{r.projectCode ? <span className="cr-dow">· {r.projectCode}</span> : null}</>
        : <span className="na">—</span> },
    { field: 'siteAddress', header: 'Where', width: 190,
      text: r => r.siteAddress ?? '',
      render: r => r.siteAddress || <span className="na">—</span> },
    { field: 'scheduledDate', header: 'Scheduled', width: 170,
      text: r => scheduledText(r),
      render: r => scheduledText(r) || <span className="na">—</span> },
    { field: 'assignedToName', header: 'Driver', width: 170,
      text: r => r.assignedToName ?? 'Unassigned',
      render: r => r.assignedToName ?? <span className="na">Unassigned</span> },
    { field: 'routeCode', header: 'Route', width: 140,
      text: r => r.routeCode ?? '',
      render: r => r.routeCode ?? <span className="na">—</span> },
    { field: 'jobNo', header: 'Job', width: 130,
      // Minted by Sample Registration on eCOC validation — it belongs HERE, on
      // the historical view, because by this point in a service's life it exists.
      text: r => r.jobNo ?? '',
      render: r => r.jobNo ?? <span className="na">—</span> },
    { field: 'stage', header: 'Stage', width: 130, sortable: false,
      text: r => serviceStage(r).label,
      render: r => {
        const s = serviceStage(r);
        return <span className={`cr-chip ${s.tone}`}>{s.label}</span>;
      } },
  ], [catName, catTone]);

  const open = openId ? rows.find(r => r.id === openId) ?? null : null;
  if (open) return (
    <TrackingDetail
      row={open}
      catName={catName}
      onBack={() => setOpenId(null)}
      onChanged={() => load()}
    />
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Client Request</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Service Tracking</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="svctrack" loadMs={loadMs} loading={loading}
                       auditFormName={AUDIT_FORM_NAME} />
          </div>
          <div className="dp-subtitle">
            Historical view · {describeRange(SERVICE_RANGES, days, custom)} — {filtered.length} of {rows.length} services.
          </div>
        </div>

        <AppToolbar
          onRefresh={() => void load()}
          refreshing={loading}
          refreshDisabled={loading}
          exportData={{ ...gridExportMatrix(filtered, columns), filename: 'service-tracking' }}
        />

        <div className="ct-filters">
          <DateRangeSelect variant="combo" ranges={SERVICE_RANGES}
                           value={days} onChange={setDays}
                           custom={custom} onCustomChange={setCustom} />
          <input className="ct-search" value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="Search request, client, address, driver or job"
                 aria-label="Search service tracking" />
          <select className="ct-sel" value={cat} onChange={e => setCat(e.target.value)}
                  aria-label="Category">
            <option value="">All categories</option>
            {defs.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
          </select>
          <select className="ct-sel" value={stage} onChange={e => setStage(e.target.value)}
                  aria-label="Stage">
            <option value="">All stages</option>
            {['Unscheduled', 'Scheduled', 'Dispatched', 'On site', 'Completed']
              .map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

      </div>

      <div className="bl-grid-area">
        <div className="bl-card bl-card-flex">
          <AppDataGrid
            rows={filtered}
            columns={columns}
            getRowId={r => r.id}
            defaultPageSize={20}
            loading={loading}
            selectable
            selectedIds={sel}
            onSelectionChange={setSel}
            onRowClick={r => setOpenId(r.id)}
            emptyText={rows.length
              ? 'Nothing in this window — widen the date range.'
              : 'No services yet — register a request and add a service.'}
          />
        </div>
      </div>
    </div>
  );
}

/** `MM/DD/YYYY · HH:mm` when a time was agreed, date alone when it was not —
 *  via the shared helpers, never inline (§14b.6). */
function scheduledText(r: ServiceTrackingRow): string {
  if (!r.scheduledDate) return '';
  const date = fmtLocalDate(r.scheduledDate);
  const t = (r.scheduledTime ?? '').slice(0, 5);
  return t ? `${date} · ${t}` : date;
}

/**
 * The read-only service record, with its lifecycle beside it.
 *
 * The rail is the point of this view: a row in the list says what stage a
 * service reached, this says WHEN it reached each one, and which steps it has
 * not taken yet. Both read the same `serviceStage`, so the ticked steps and the
 * Stage chip can never tell different stories.
 */
function TrackingDetail({ row, catName, onBack, onChanged }: {
  row: ServiceTrackingRow;
  catName: (code?: string | null) => string;
  onBack: () => void;
  /** Re-reads the list after a field step is recorded, so the row, its stage
   *  and the lifecycle rail all show the new stamp together. */
  onChanged: () => void | Promise<void>;
}) {
  const stage = serviceStage(row);
  const { confirm } = useConfirm();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  /* Record arrival / Record completion (v68 markArrived / markDone). Confirmed
     through the shared dialog like every state change (standing rule 7), in
     v68's words; the server enforces the order and says why when it refuses. */
  const record = async (step: 'arrived' | 'completed') => {
    const ok = await confirm(step === 'arrived'
      ? { title: 'Record arrival on site?',
          message: 'This is what the Field App reports when the technician presses '
                 + '"I have arrived". It is recorded now, and audited.' }
      : { title: 'Record completion?',
          message: 'This marks the work on site as finished. It is recorded now, and audited.' });
    if (!ok) return;
    setBusy(true);
    try {
      const at = await recordServiceProgress(row.id, step);
      notify.success(step === 'arrived'
        ? `Arrival recorded — on site at ${fmtLocalDateTime(at)}.`
        : `Completion recorded at ${fmtLocalDateTime(at)}.`);
      await onChanged();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Could not record this step.');
    } finally {
      setBusy(false);
    }
  };


  /* When each step happened. "Requested" comes from the parent request, and the
     later three from the service's own stamps; a step with no time either has
     not happened or was not stamped, and both read honestly as "not yet". */
  const stamps: (string | null)[] = [
    fmtLocalDate(row.requestedAt),
    row.scheduledDate ? scheduledText(row) : null,
    row.routeCode ?? (row.routeId ? 'Routed' : null),
    row.arrivedAt ? fmtLocalDateTime(row.arrivedAt) : null,
    row.completedAt ? fmtLocalDateTime(row.completedAt) : null,
  ];

  const line = (label: string, value: React.ReactNode) => (
    <div className="ct-rec-row">
      <span className="ct-rec-k">{label}</span>
      <span className="ct-rec-v">{value || <span className="na">—</span>}</span>
    </div>
  );

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the detail
  // panel's inline actions.  This whole component only mounts while the panel is
  // shown, so the keys are naturally gated to it; each button also renders only
  // in a given state (Record arrival before an arrival is stamped; Record
  // completion once arrived, before completion), so gate each def to match and
  // mirror its `disabled` (busy).  Alt+A = Record arrival · Alt+C = Record completion.
  useHotkeys([
    ...(!row.arrivedAt
      ? [{ combo: 'alt+a', label: 'Record arrival', group: 'Client Request',
           run: () => void record('arrived'), disabled: busy }]
      : []),
    ...(row.arrivedAt && !row.completedAt
      ? [{ combo: 'alt+c', label: 'Record completion', group: 'Client Request',
           run: () => void record('completed'), disabled: busy }]
      : []),
  ]);

  return (
    <div className="bl-page mlv-host cr-detail">
      <div className="bl-grid-area ct-dpage">
        {/* ONE header, laid out like prototype v68 (owner request 2026-09-11):
            back · breadcrumb on the first line, the record's name, whose it is,
            its stage and the way onward on the second. This screen DEPARTS
            from the shared sd-head + action bar (BO UI Standard §4) on purpose:
            it is a read-only record with no Save trio for the bar to carry, and
            split three ways the header left an empty grey band with its only
            buttons stranded at the right. No prev/next record arrows (owner
            request 2026-09-11) — v68 has none. The header belongs to the MAIN
            column, as in v68, so the Lifecycle / Audit rail starts at the top
            beside it; and the page sits on the page background with the
            record as a white card, not a card inside one big white box. */}
        <div className="ct-detail">
          <div className="ct-main">
            <div className="ct-dh">
              <div className="ct-dh-crumb">
                <button type="button" className="ct-dh-back" onClick={onBack}
                        title="Back to list" aria-label="Back to Service Tracking">
                  <i className="ti ti-arrow-left" />
                </button>
                <nav className="ct-dh-bc" aria-label="Breadcrumb">
                  <button type="button" className="ct-dh-link" onClick={onBack}>
                    Service Tracking
                  </button>
                  <i className="ti ti-chevron-right" />
                  <b>{row.serviceNo ?? '—'}</b>
                </nav>
              </div>
              <div className="ct-dh-row">
                <div className="ct-dh-txt">
                  <h2 className="ct-dh-t">
                    {catName(row.categoryCode)}{row.serviceType ? ` · ${row.serviceType}` : ''}
                  </h2>
                  <div className="ct-dh-sub">
                    {[row.clientName, row.projectCode].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <div className="ct-dh-acts">
                  {/* A STATE beside the way onward, as v68 sets them. */}
                  <span className={`cr-chip ${stage.tone}`}>{stage.label}</span>
                  {/* The request is where everything about this service is
                      editable; this opens THAT request's detail, not the list. */}
                  <button type="button" className="ca-btn"
                          onClick={() => navigateToPage('reqreg', row.requestId)}>
                    <span className="tb-i"><i className="ti ti-external-link" /></span>
                    Open request
                  </button>
                </div>
              </div>
            </div>

          <div className="bl-card ct-rec">
            <h3 className="ct-rec-h">Service record</h3>
            <div className="ct-rec-tbl">
              {line('Service ID', row.serviceNo)}
              {line('Request', row.requestNo)}
              {/* The category alone: the heading already reads
                  "Category · Service type", as v68's does. */}
              {line('Category', catName(row.categoryCode))}
              {line('Where', row.siteAddress)}
              {line('Scheduled', scheduledText(row))}
              {line('Driver', row.assignedToName)}
              {line('Route', row.routeCode
                ? `${row.routeCode}${row.routeName ? ` · ${row.routeName}` : ''}` : null)}
              {/* v68: an unrecorded step is an ACTION, not a blank — until the
                  Field App reports it, dispatch records it here. */}
              {line('Arrived', row.arrivedAt
                ? fmtLocalDateTime(row.arrivedAt)
                : (
                  <button type="button" className="ca-btn ct-rec-btn" disabled={busy}
                          title={`Record arrival (${formatCombo('alt+a')})`}
                          onClick={() => void record('arrived')}>
                    <span className="tb-i"><i className="ti ti-map-pin" /></span>
                    Record arrival
                  </button>
                ))}
              {/* Completion only after arrival (v68 markDone) — before it, the
                  step cannot happen yet, and saying so is more use than a dash. */}
              {line('Completed', row.completedAt
                ? fmtLocalDateTime(row.completedAt)
                : row.arrivedAt
                  ? (
                    <button type="button" className="ca-btn ct-rec-btn" disabled={busy}
                            title={`Record completion (${formatCombo('alt+c')})`}
                            onClick={() => void record('completed')}>
                      <span className="tb-i"><i className="ti ti-check" /></span>
                      Record completion
                    </button>
                  )
                  : <span className="na">after arrival</span>)}
              {line('Job', row.jobNo)}
            </div>
          </div>
        </div>

        <aside className="ct-rail">
          <div className="bl-card ct-rail-card">
            <h4 className="ct-rail-h">Lifecycle</h4>
            <ol className="ct-life">
              {SERVICE_STAGES.map((s, i) => (
                <li key={s} className={`ct-life-step${i <= stage.index ? ' on' : ''}`}>
                  <span className="ct-life-dot" aria-hidden="true" />
                  <span className="ct-life-lbl">{s}</span>
                  {/* The stamp shares the label's line (v46). */}
                  {/* A step the service has already PASSED but that carries no
                      stamp is "not recorded", not "not yet": v46 ticks every
                      step up to the current stage, so a completed service shows
                      Dispatched as done — and labelling that "not yet" beside a
                      ticked dot contradicts itself. (Here it is honest: this
                      lab has no routes, so nothing was ever dispatched.) */}
                  <span className="ct-life-when">
                    {stamps[i] ?? (i <= stage.index ? 'not recorded' : 'not yet')}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* The record's own history, beside the record (v46). It reads the
              SHARED audit endpoint — no second source of truth — and is scoped
              to THIS service by the ServiceNo its rows are prefixed with
              (§11.2), because the audit Source is the owning REQUEST so one
              history covers the whole request. */}
          <ServiceAuditPanel requestId={row.requestId} serviceNo={row.serviceNo} />
              </aside>
        </div>
      </div>
    </div>
  );
}

/**
 * The service's own audit history, shown beside the record (prototype v46).
 *
 * <p>Reads the ONE shared audit endpoint — the same rows the Audit Trail Log
 * and the toolbar's audit popup show, never a second query with its own idea of
 * what happened (standing rule 8).</p>
 *
 * <p><b>Why it filters client-side.</b> Audit rows for a service are written
 * with <c>Source</c> = the owning REQUEST, so one history covers the whole
 * request (§11.1), and each row's property is prefixed with the service's
 * PERMANENT Service ID (§11.2). So the request's rows are fetched and narrowed
 * to this service by that prefix. A service with no number yet has never been
 * saved and therefore has no history to show.</p>
 */
function ServiceAuditPanel({ requestId, serviceNo }: {
  requestId: string;
  serviceNo: string | null;
}) {
  const [rows, setRows] = useState<AuditTrailRow[]>([]);
  const [busy, setBusy] = useState(true);
  const [err,  setErr]  = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr(null);
    fetchAuditTrail(AUDIT_FORM_NAME, requestId)
      .then(all => {
        if (!alive) return;
        const prefix = serviceNo ? `${serviceNo} · ` : null;
        setRows(prefix
          ? all.filter(r => (r.propertyName ?? '').startsWith(prefix))
          : []);
      })
      // Not swallowed: a history that failed to load must not look like a
      // history that is empty — those are opposite claims.
      .catch(() => { if (alive) setErr('Could not load the audit history.'); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [requestId, serviceNo]);

  // The Service ID prefix is stripped: every row here belongs to the same
  // service, so repeating it on all of them pushes the property out of view.
  const shown = useMemo(() => rows.map(r => ({
    ...r,
    propertyName: (r.propertyName ?? '').replace(`${serviceNo} · `, '') || null,
  })), [rows, serviceNo]);

  return (
    <div className="bl-card ct-rail-card">
      <h4 className="ct-rail-h">Audit</h4>
      {/* The app's ONE audit timeline (features/audit/AuditTimeline — the one
          the Audit Trail Log and every toolbar audit popup draw), not a third
          rendering of the same rows (standing rule 8) — in its own LAYOUT,
          recoloured to v68's audit palette (orange dot, amber pill, red/green
          values) at the owner's request: colours, not layout. A failed load
          keeps its own message — an error must not read as an empty history. */}
      {err ? (
        <div className="ct-audit-empty">{err}</div>
      ) : (
        <AuditTimeline rows={shown} loading={busy} palette="v68"
          emptyText="Nothing recorded yet. Change a field and save — each altered property is written as its own audit row." />
      )}
    </div>
  );
}
